import React, { useState } from 'react';
import { Button, message, Skeleton, Space, Table, Tag } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, ClockCircleOutlined } from '@ant-design/icons';
import type { ColumnsType, ColumnType, TablePaginationConfig } from 'antd/es/table';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import TablePaginationBar from '../../../../../components/TablePaginationBar';
import AsyncReportDownloadModal, {
    dashboardAsyncReportLabelI18nKeys,
} from '../../../../../components/asyncReportDownload';
import TransportKpiCard from './TransportKpiCard';
import TransportDetailDrawer, { type TransportDetailGranularity } from './TransportDetailDrawer';
import {
    GET_INHUB_TRANSPORT_KPI_SUMMARY,
    type GetInhubTransportKpiSummaryResponse,
    type InhubTransportSectionKpiResp
} from '../graphql/queries/getInhubTransportKpiSummary';
import {
    GET_INHUB_TRANSPORT_ROUTE_SUMMARIES_PAGE,
    type GetInhubTransportRouteSummariesPageResponse,
    type InhubTransportRouteOrderField,
    type InhubTransportRouteRowResp,
    type SortDirection
} from '../graphql/queries/getInhubTransportRouteSummariesPage';
import {
    GENERATE_INHUB_TRANSPORT_ROUTE_SUMMARIES_EXPORT,
    type GenerateInhubTransportRouteSummariesExportResponse,
    type GenerateInhubTransportRouteSummariesExportVariables
} from '../graphql/mutations/generateInhubTransportRouteSummariesExport';
import type { InhubTransportDetailStatusFilter } from '../graphql/queries/getInhubTransportBagDetailPage';
import type { InhubTransportPerspective } from '../graphql/types';

const I18N_BASE = 'wms.delivery.inhubDashboard.transport';
const DEFAULT_PAGE_SIZE = 10;

const COLUMN_TO_ORDER_FIELD: Record<string, InhubTransportRouteOrderField> = {
    route: 'PEER_HUB_ID',
    departedBagCount: 'DEPARTED_BAG_COUNT',
    departedParcelCount: 'DEPARTED_PARCEL_COUNT',
    arrivedBagCount: 'ARRIVED_BAG_COUNT',
    arrivedParcelCount: 'ARRIVED_PARCEL_COUNT',
    notArrivedBagCount: 'NOT_ARRIVED_BAG_COUNT',
    notArrivedParcelCount: 'NOT_ARRIVED_PARCEL_COUNT'
};

interface KpiSectionProps {
    title: string;
    data: InhubTransportSectionKpiResp;
    perspective: InhubTransportPerspective;
    onKpiDrilldown: (
        perspective: InhubTransportPerspective,
        statusFilter: InhubTransportDetailStatusFilter,
        granularity: TransportDetailGranularity,
    ) => void;
}

const KpiSection: React.FC<KpiSectionProps> = ({ title, data, perspective, onKpiDrilldown }) => {
    const { t } = useTranslation();
    const perspectiveKey = perspective === 'OUTBOUND' ? 'outbound' : 'inbound';
    const dispatchedTitle = t(`${I18N_BASE}.dispatched.${perspectiveKey}`);
    const arrivedTitle = t(`${I18N_BASE}.arrived.${perspectiveKey}`);
    const notArrivedTitle = t(`${I18N_BASE}.notArrived.${perspectiveKey}`);
    const dispatchedTooltip = t(`${I18N_BASE}.indicatorDefs.dispatched.${perspectiveKey}`);
    const arrivedTooltip = t(`${I18N_BASE}.indicatorDefs.arrived.${perspectiveKey}`);
    const notArrivedTooltip = t(`${I18N_BASE}.indicatorDefs.notArrived.${perspectiveKey}`);

    return (
        <div className="flex flex-col gap-2">
            <h3 className="text-sm font-bold text-gray-800 m-0">{title}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <TransportKpiCard
                    title={dispatchedTitle}
                    cell={data.departed}
                    definitionTooltip={dispatchedTooltip}
                    onParcelDrilldown={() => onKpiDrilldown(perspective, 'DEPARTED', 'PARCEL')}
                    onBagDrilldown={() => onKpiDrilldown(perspective, 'DEPARTED', 'BAG')}
                />
                <TransportKpiCard
                    title={arrivedTitle}
                    cell={data.arrived}
                    definitionTooltip={arrivedTooltip}
                    hideWow
                    onParcelDrilldown={() => onKpiDrilldown(perspective, 'ARRIVED', 'PARCEL')}
                    onBagDrilldown={() => onKpiDrilldown(perspective, 'ARRIVED', 'BAG')}
                />
                <TransportKpiCard
                    title={notArrivedTitle}
                    cell={data.notArrived}
                    rateLabelKey="notArrivalRate"
                    definitionTooltip={notArrivedTooltip}
                    hideWow
                    onParcelDrilldown={() => onKpiDrilldown(perspective, 'NOT_ARRIVED', 'PARCEL')}
                    onBagDrilldown={() => onKpiDrilldown(perspective, 'NOT_ARRIVED', 'BAG')}
                />
            </div>
        </div>
    );
};

interface Props {
    hubId: number;
    rangeStart: string;
    rangeEnd: string;
}

interface DrawerState {
    open: boolean;
    granularity: TransportDetailGranularity;
    statusFilter: InhubTransportDetailStatusFilter;
    perspective: InhubTransportPerspective;
    fromHubId: number | null;
    fromHubName: string | null;
    toHubId: number | null;
    toHubName: string | null;
}

const TransportManagementTab: React.FC<Props> = ({ hubId, rangeStart, rangeEnd }) => {
    const { t } = useTranslation();
    const [perspective, setPerspective] = useState<InhubTransportPerspective>('OUTBOUND');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [orderBy, setOrderBy] = useState<InhubTransportRouteOrderField>('PEER_HUB_ID');
    const [orderDir, setOrderDir] = useState<SortDirection>('ASC');
    const [drawer, setDrawer] = useState<DrawerState | null>(null);

    const openDrawer = (
        row: InhubTransportRouteRowResp,
        granularity: TransportDetailGranularity,
        statusFilter: InhubTransportDetailStatusFilter,
        count: number
    ) => {
        if (count <= 0) return;
        setDrawer({
            open: true,
            granularity,
            statusFilter,
            perspective,
            fromHubId: row.fromHubId,
            fromHubName: row.fromHubName,
            toHubId: row.toHubId,
            toHubName: row.toHubName
        });
    };

    const closeDrawer = () => setDrawer((s) => (s ? { ...s, open: false } : s));

    const openKpiDrilldown = (
        drilldownPerspective: InhubTransportPerspective,
        statusFilter: InhubTransportDetailStatusFilter,
        granularity: TransportDetailGranularity,
    ) => {
        setDrawer({
            open: true,
            granularity,
            statusFilter,
            perspective: drilldownPerspective,
            fromHubId: drilldownPerspective === 'OUTBOUND' ? hubId : null,
            fromHubName: null,
            toHubId: drilldownPerspective === 'INBOUND' ? hubId : null,
            toHubName: null,
        });
    };

    const renderDrillNumber = (
        value: number,
        row: InhubTransportRouteRowResp,
        granularity: TransportDetailGranularity,
        statusFilter: InhubTransportDetailStatusFilter,
        highlightClass?: string
    ) => {
        if (value <= 0) {
            return <span className={highlightClass}>{Number(value).toLocaleString()}</span>;
        }
        return (
            <Button
                type="link"
                size="small"
                className={`p-0 h-auto ${highlightClass ?? ''}`}
                onClick={() => openDrawer(row, granularity, statusFilter, value)}
            >
                {Number(value).toLocaleString()}
            </Button>
        );
    };

    const { data: kpiData, loading: kpiLoading } = useQuery<GetInhubTransportKpiSummaryResponse>(
        GET_INHUB_TRANSPORT_KPI_SUMMARY,
        {
            variables: { filter: { hubId, rangeStart, rangeEnd } },
            errorPolicy: 'all',
            notifyOnNetworkStatusChange: true
        }
    );

    const summary = kpiData?.getInhubTransportKpiSummary;
    const calculatedAtIso = summary?.dataCalculatedAtMax ?? null;
    const calculatedAtLabel = calculatedAtIso
        ? dayjs(calculatedAtIso).format('YYYY-MM-DD HH:mm:ss')
        : '-';

    const { data: routeData, loading: routeLoading } = useQuery<GetInhubTransportRouteSummariesPageResponse>(
        GET_INHUB_TRANSPORT_ROUTE_SUMMARIES_PAGE,
        {
            variables: { filter: { hubId, rangeStart, rangeEnd, perspective, page, pageSize, orderBy, orderDir } },
            errorPolicy: 'all',
            notifyOnNetworkStatusChange: true
        }
    );

    const routeRows = routeData?.getInhubTransportRouteSummariesPage?.content ?? [];
    const routeTotal = routeData?.getInhubTransportRouteSummariesPage?.totalElements ?? 0;

    const [exportMutation, { loading: exportLoading }] = useMutation<
        GenerateInhubTransportRouteSummariesExportResponse,
        GenerateInhubTransportRouteSummariesExportVariables
    >(GENERATE_INHUB_TRANSPORT_ROUTE_SUMMARIES_EXPORT);
    const [downloadModalVisible, setDownloadModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');

    const handleExport = async () => {
        try {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const result = await exportMutation({
                variables: {
                    filter: { hubId, rangeStart, rangeEnd, perspective, page: 1, pageSize: 1, orderBy, orderDir },
                    timezone: tz
                }
            });
            const fileName = result.data?.generateInhubTransportRouteSummariesExport;
            if (fileName) {
                setGeneratedFileName(fileName);
                setDownloadModalVisible(true);
            }
        } catch (e: unknown) {
            const errMsg = e instanceof Error ? e.message : String(e);
            message.error(errMsg);
        }
    };

    const unitParcel = t(`${I18N_BASE}.units.parcel`);
    const unitBag = t(`${I18N_BASE}.units.bag`);

    const perspectiveKey = perspective === 'OUTBOUND' ? 'outbound' : 'inbound';
    const dispatchedLabel = t(`${I18N_BASE}.dispatched.${perspectiveKey}`);
    const arrivedLabel = t(`${I18N_BASE}.arrived.${perspectiveKey}`);
    const notArrivedLabel = t(`${I18N_BASE}.notArrived.${perspectiveKey}`);

    const sortOrderOf = (
        field: InhubTransportRouteOrderField
    ): ColumnType<InhubTransportRouteRowResp>['sortOrder'] => {
        if (orderBy !== field) return null;
        return orderDir === 'ASC' ? 'ascend' : 'descend';
    };

    const columns: ColumnsType<InhubTransportRouteRowResp> = [
        {
            title: t(`${I18N_BASE}.columns.route`),
            dataIndex: 'route',
            width: 220,
            fixed: 'left' as const,
            align: 'left' as const,
            sorter: true,
            sortOrder: sortOrderOf('PEER_HUB_ID'),
            render: (_: unknown, r: InhubTransportRouteRowResp) => (
                <span>
                    {r.fromHubName} → {r.toHubName}
                    {r.status === 'OVER_72H' && (
                        <Tag color="red" bordered={false} icon={<ClockCircleOutlined />} className="ml-2">
                            {t(`${I18N_BASE}.status.over72h`)}
                        </Tag>
                    )}
                </span>
            )
        },
        {
            title: dispatchedLabel,
            children: [
                {
                    title: unitBag,
                    dataIndex: 'departedBagCount',
                    width: 90,
                    align: 'left' as const,
                    sorter: true,
                    sortOrder: sortOrderOf('DEPARTED_BAG_COUNT'),
                    render: (v: number, r: InhubTransportRouteRowResp) => renderDrillNumber(v, r, 'BAG', 'DEPARTED')
                },
                {
                    title: unitParcel,
                    dataIndex: 'departedParcelCount',
                    width: 100,
                    align: 'left' as const,
                    sorter: true,
                    sortOrder: sortOrderOf('DEPARTED_PARCEL_COUNT'),
                    render: (v: number, r: InhubTransportRouteRowResp) => renderDrillNumber(v, r, 'PARCEL', 'DEPARTED')
                }
            ]
        },
        {
            title: arrivedLabel,
            children: [
                {
                    title: unitBag,
                    dataIndex: 'arrivedBagCount',
                    width: 90,
                    align: 'left' as const,
                    sorter: true,
                    sortOrder: sortOrderOf('ARRIVED_BAG_COUNT'),
                    render: (v: number, r: InhubTransportRouteRowResp) =>
                        renderDrillNumber(v, r, 'BAG', 'ARRIVED')
                },
                {
                    title: unitParcel,
                    dataIndex: 'arrivedParcelCount',
                    width: 100,
                    align: 'left' as const,
                    sorter: true,
                    sortOrder: sortOrderOf('ARRIVED_PARCEL_COUNT'),
                    render: (v: number, r: InhubTransportRouteRowResp) =>
                        renderDrillNumber(v, r, 'PARCEL', 'ARRIVED')
                }
            ]
        },
        {
            title: notArrivedLabel,
            children: [
                {
                    title: unitBag,
                    dataIndex: 'notArrivedBagCount',
                    width: 90,
                    align: 'left' as const,
                    sorter: true,
                    sortOrder: sortOrderOf('NOT_ARRIVED_BAG_COUNT'),
                    render: (v: number, r: InhubTransportRouteRowResp) =>
                        renderDrillNumber(v, r, 'BAG', 'NOT_ARRIVED', v > 0 ? 'text-red-500' : undefined)
                },
                {
                    title: unitParcel,
                    dataIndex: 'notArrivedParcelCount',
                    width: 100,
                    align: 'left' as const,
                    sorter: true,
                    sortOrder: sortOrderOf('NOT_ARRIVED_PARCEL_COUNT'),
                    render: (v: number, r: InhubTransportRouteRowResp) =>
                        renderDrillNumber(v, r, 'PARCEL', 'NOT_ARRIVED', v > 0 ? 'text-red-500' : undefined)
                }
            ]
        },
        {
            title: t(`${I18N_BASE}.columns.over72hBagCount`),
            children: [
                {
                    title: unitBag,
                    dataIndex: 'over72hUnarrivedBagCount',
                    width: 90,
                    align: 'left' as const,
                    render: (v: number, r: InhubTransportRouteRowResp) =>
                        renderDrillNumber(v, r, 'BAG', 'OVER_72H', v > 0 ? 'text-red-500 font-semibold' : undefined)
                }
            ]
        }
    ];

    const handleTableChange = (
        _pagination: TablePaginationConfig,
        _filters: Record<string, FilterValue | null>,
        sorter: SorterResult<InhubTransportRouteRowResp> | SorterResult<InhubTransportRouteRowResp>[]
    ) => {
        const single = Array.isArray(sorter) ? sorter[0] : sorter;
        const field = typeof single?.field === 'string' ? single.field : undefined;
        const nextField = field ? COLUMN_TO_ORDER_FIELD[field] : undefined;
        if (!nextField || !single?.order) {
            setOrderBy('PEER_HUB_ID');
            setOrderDir('ASC');
        } else {
            setOrderBy(nextField);
            setOrderDir(single.order === 'ascend' ? 'ASC' : 'DESC');
        }
        setPage(1);
    };

    return (
        <div className="flex flex-col gap-3">
            {kpiLoading && !summary ? (
                <Skeleton active paragraph={{ rows: 4 }} />
            ) : (
                <>
                    {summary && (
                        <KpiSection
                            title={t(`${I18N_BASE}.perspective.outbound`)}
                            data={summary.outbound}
                            perspective="OUTBOUND"
                            onKpiDrilldown={openKpiDrilldown}
                        />
                    )}
                    {summary && (
                        <KpiSection
                            title={t(`${I18N_BASE}.perspective.inbound`)}
                            data={summary.inbound}
                            perspective="INBOUND"
                            onKpiDrilldown={openKpiDrilldown}
                        />
                    )}
                </>
            )}

            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3 flex-wrap">
                    <Space>
                        <Button
                            type={perspective === 'OUTBOUND' ? 'primary' : 'default'}
                            icon={<ArrowUpOutlined />}
                            onClick={() => { setPerspective('OUTBOUND'); setPage(1); }}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t(`${I18N_BASE}.perspective.outbound`)}</span>
                        </Button>
                        <Button
                            type={perspective === 'INBOUND' ? 'primary' : 'default'}
                            icon={<ArrowDownOutlined />}
                            onClick={() => { setPerspective('INBOUND'); setPage(1); }}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t(`${I18N_BASE}.perspective.inbound`)}</span>
                        </Button>
                    </Space>
                    {calculatedAtIso && (
                        <span className="text-sm text-red-500 font-bold">
                            {t('wms.delivery.inhubDashboard.dataUpdatedAt')} {calculatedAtLabel}
                        </span>
                    )}
                </div>
                <Button
                    type="primary"
                    loading={exportLoading}
                    onClick={handleExport}
                    disabled={routeTotal === 0}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{t(`${I18N_BASE}.export.button`)}</span>
                </Button>
            </div>

            <Table<InhubTransportRouteRowResp>
                rowKey={(r) => `${r.fromHubId}-${r.toHubId}`}
                columns={columns}
                dataSource={routeRows}
                pagination={false}
                size="middle"
                scroll={{ x: 1300 }}
                className="mb-2"
                loading={routeLoading}
                onChange={handleTableChange}
                showSorterTooltip={false}
                rowClassName={(r) => (r.status === 'OVER_72H' ? 'bg-orange-50 [&>td]:!bg-orange-50' : '')}
            />
            <p className="text-black mb-2">
                {t(`${I18N_BASE}.tableHint`)}
            </p>
            <TablePaginationBar
                total={routeTotal}
                current={page}
                pageSize={pageSize}
                onChange={(p, ps) => {
                    setPage(p);
                    setPageSize(ps);
                }}
                onShowSizeChange={(_, ps) => {
                    setPage(1);
                    setPageSize(ps);
                }}
            />

            {drawer && (
                <TransportDetailDrawer
                    open={drawer.open}
                    onClose={closeDrawer}
                    granularity={drawer.granularity}
                    hubId={hubId}
                    rangeStart={rangeStart}
                    rangeEnd={rangeEnd}
                    fromHubId={drawer.fromHubId}
                    fromHubName={drawer.fromHubName}
                    toHubId={drawer.toHubId}
                    toHubName={drawer.toHubName}
                    perspective={drawer.perspective}
                    statusFilter={drawer.statusFilter}
                />
            )}

            <AsyncReportDownloadModal
                visible={downloadModalVisible}
                onClose={() => setDownloadModalVisible(false)}
                fileName={generatedFileName}
                reportLabelI18nKey={dashboardAsyncReportLabelI18nKeys.inhubTransport}
            />
        </div>
    );
};

export default TransportManagementTab;
